<template>
  <div v-if="hasValidData">
    <apexchart
      ref="chart"
      :type="type"
      :height="height"
      :options="chartOptions"
      :series="renderSeries"
    />
  </div>
  <div v-else class="no-data-message">
    <p>Nenhum dado disponível</p>
  </div>
</template>

<script>
import VueApexCharts from "vue-apexcharts";
import { colors, commonOptions } from "./commons";

const percentFormatter = new Intl.NumberFormat("pt-BR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatPercent(value) {
  if (!isFinite(value)) return "0,00%";
  return `${percentFormatter.format(value)}%`;
}

function safeNumber(v) {
  return typeof v === "number" && !isNaN(v) ? v : 0;
}

function convertSeries(rawSeries, basis) {
  if (!Array.isArray(rawSeries) || rawSeries.length === 0) return rawSeries;

  if (basis === "category") {
    const lengths = rawSeries.map((s) => (s.data ? s.data.length : 0));
    const maxLen = Math.max(0, ...lengths);
    const columnTotals = [];
    for (let i = 0; i < maxLen; i++) {
      let sum = 0;
      for (const s of rawSeries) {
        sum += safeNumber(s.data?.[i]);
      }
      columnTotals.push(sum);
    }
    return rawSeries.map((s) => ({
      ...s,
      data: (s.data || []).map((v, i) => {
        const total = columnTotals[i];
        return total > 0 ? +((safeNumber(v) / total) * 100).toFixed(2) : 0;
      }),
    }));
  }

  if (basis === "series") {
    return rawSeries.map((s) => {
      const total = (s.data || []).reduce((sum, v) => sum + safeNumber(v), 0);
      return {
        ...s,
        data: (s.data || []).map((v) =>
          total > 0 ? +((safeNumber(v) / total) * 100).toFixed(2) : 0
        ),
      };
    });
  }

  // basis === 'total'
  let grand = 0;
  for (const s of rawSeries) {
    for (const v of s.data || []) grand += safeNumber(v);
  }
  return rawSeries.map((s) => ({
    ...s,
    data: (s.data || []).map((v) =>
      grand > 0 ? +((safeNumber(v) / grand) * 100).toFixed(2) : 0
    ),
  }));
}

export default {
  name: "ModernBarChart",
  components: {
    apexchart: VueApexCharts,
  },
  props: {
    title: {
      type: String,
      default: "",
    },
    series: {
      type: Array,
      default: () => [],
    },
    categories: {
      type: Array,
      default: () => [],
    },
    colors: {
      type: Array,
      default: null,
    },
    height: {
      type: [String, Number],
      default: 350,
    },
    stacked: {
      type: Boolean,
      default: false,
    },
    horizontal: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "bar",
    },
    displayMode: {
      type: String,
      default: "absolute",
      validator: (v) => ["absolute", "percent"].includes(v),
    },
    percentBasis: {
      type: String,
      default: "category",
      validator: (v) => ["category", "series", "total"].includes(v),
    },
  },
  data() {
    const vm = this;
    return {
      chartOptions: commonOptions.apply({
        title: {
          text: this.title,
        },
        chart: {
          type: this.type,
          stacked: this.stacked,
          toolbar: {
            show: false,
          },
          animations: {
            enabled: true,
            speed: 800,
            easing: "easeinout",
            dynamicAnimation: {
              enabled: true,
              speed: 800,
            },
            animateGradually: {
              enabled: true,
              delay: 80,
            },
          },
          redrawOnParentResize: true,
        },
        plotOptions: {
          bar: {
            horizontal: this.horizontal,
            borderRadius: 6,
            columnWidth: "70%",
            dataLabels: {
              position: "top",
            },
          },
        },
        colors: this.colors || colors.default(),
        dataLabels: {
          enabled: this.type === "bar",
          formatter: function (val) {
            if (vm.displayMode === "percent") {
              return formatPercent(safeNumber(val));
            }
            return val;
          },
          style: {
            fontSize: "12px",
            fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
            fontWeight: "600",
            colors: ["#ffffff"],
          },
        },
        stroke: {
          show: true,
          width: this.type === "line" ? 3 : 2,
          colors: this.type === "line" ? undefined : ["transparent"],
          curve: this.type === "line" ? "smooth" : "straight",
        },
        markers: {
          size: this.type === "line" ? 4 : 0,
          strokeWidth: 2,
          strokeOpacity: 0.9,
          fillOpacity: 1,
          hover: {
            size: this.type === "line" ? 6 : 0,
          },
        },
        xaxis: {
          categories: this.categories,
          labels: {
            style: {
              fontSize: "12px",
              fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
              fontWeight: "500",
              colors: "#64748b",
            },
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          labels: {
            style: {
              fontSize: "12px",
              fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
              fontWeight: "500",
              colors: "#64748b",
            },
            formatter: function (val) {
              if (vm.displayMode === "percent") {
                return `${safeNumber(val).toFixed(0)}%`;
              }
              return val;
            },
          },
        },
        grid: {
          borderColor: "#e2e8f0",
          strokeDashArray: 4,
          xaxis: {
            lines: {
              show: true,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
        tooltip: {
          theme: "light",
          style: {
            fontSize: "12px",
            fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
          },
          y: {
            formatter: function (val, opts) {
              const seriesIndex = opts.seriesIndex;
              const dataPointIndex = opts.dataPointIndex;
              const rawSeries = vm.series;
              let absolute = val;
              if (
                Array.isArray(rawSeries) &&
                rawSeries[seriesIndex] &&
                Array.isArray(rawSeries[seriesIndex].data)
              ) {
                absolute = safeNumber(
                  rawSeries[seriesIndex].data[dataPointIndex]
                );
              }
              const pct = vm.percentForPoint(seriesIndex, dataPointIndex);
              return `${absolute} (${formatPercent(pct)})`;
            },
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "right",
          fontSize: "12px",
          fontFamily: "'Inter', 'Helvetica Neue', sans-serif",
          fontWeight: "500",
          markers: {
            width: 12,
            height: 12,
            radius: 6,
          },
        },
        fill: {
          opacity: 1,
          type: this.type === "line" ? "solid" : "gradient",
          gradient: {
            shade: "light",
            type: "vertical",
            shadeIntensity: 0.25,
            gradientToColors: undefined,
            inverseColors: true,
            opacityFrom: 1,
            opacityTo: 0.85,
            stops: [0, 100],
          },
        },
      }),
    };
  },
  computed: {
    hasValidData() {
      return (
        Array.isArray(this.series) &&
        this.series.length > 0 &&
        this.series.some(
          (s) => s && Array.isArray(s.data) && s.data.length > 0
        ) &&
        Array.isArray(this.categories) &&
        this.categories.length > 0
      );
    },
    validSeries() {
      if (!Array.isArray(this.series)) return [];
      return this.series.filter(
        (s) => s && Array.isArray(s.data) && s.data.length > 0
      );
    },
    renderSeries() {
      if (this.displayMode === "percent") {
        return convertSeries(this.validSeries, this.percentBasis);
      }
      return this.validSeries;
    },
  },
  watch: {
    series: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
    categories: {
      handler() {
        this.updateChart();
      },
      deep: true,
    },
    displayMode() {
      this.updateChart();
    },
    percentBasis() {
      this.updateChart();
    },
  },
  methods: {
    percentForPoint(seriesIndex, dataPointIndex) {
      const rawSeries = this.validSeries;
      if (
        !rawSeries[seriesIndex] ||
        !Array.isArray(rawSeries[seriesIndex].data)
      )
        return 0;
      const value = safeNumber(rawSeries[seriesIndex].data[dataPointIndex]);
      if (this.percentBasis === "category") {
        let total = 0;
        for (const s of rawSeries) {
          total += safeNumber(s.data?.[dataPointIndex]);
        }
        return total > 0 ? (value / total) * 100 : 0;
      }
      if (this.percentBasis === "series") {
        const total = (rawSeries[seriesIndex].data || []).reduce(
          (sum, v) => sum + safeNumber(v),
          0
        );
        return total > 0 ? (value / total) * 100 : 0;
      }
      let grand = 0;
      for (const s of rawSeries) {
        for (const v of s.data || []) grand += safeNumber(v);
      }
      return grand > 0 ? (value / grand) * 100 : 0;
    },
    updateChart() {
      if (this.$refs.chart) {
        const validCategories =
          Array.isArray(this.categories) && this.categories.length > 0
            ? this.categories.filter((c) => c !== null && c !== undefined)
            : [];

        if (this.renderSeries.length === 0 || validCategories.length === 0) {
          return;
        }

        const yaxisMax =
          this.displayMode === "percent" &&
          (this.percentBasis === "category" || this.percentBasis === "series")
            ? 100
            : undefined;

        this.$refs.chart.updateOptions(
          {
            xaxis: {
              categories: validCategories,
            },
            colors: this.colors || colors.default(),
            yaxis: {
              max: yaxisMax,
            },
          },
          false,
          false
        );

        this.$refs.chart.updateSeries(this.renderSeries, true);
      }
    },
  },
};
</script>

<style scoped>
.no-data-message {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  color: #64748b;
  font-family: "Inter", "Helvetica Neue", sans-serif;
  font-size: 14px;
}
</style>
